package top.wcpe.allincore.ui.runtime.render

import top.wcpe.allincore.layout.engine.Box
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.render.UiRenderBridge
import top.wcpe.allincore.ui.runtime.render.model.GradientType
import top.wcpe.allincore.ui.runtime.render.model.MaskType
import top.wcpe.allincore.ui.runtime.render.model.RenderBatch
import top.wcpe.allincore.ui.runtime.render.model.RenderCommand
import top.wcpe.allincore.ui.runtime.render.model.RenderCommandType
import top.wcpe.allincore.ui.runtime.render.model.StyledFill
import top.wcpe.allincore.ui.runtime.style.StyleVisual

/**
 * Task 2a：方框类控件（Checkbox / RadioButton / TextField / Dropdown 头部）共享的「背景 + 边框」绘制 helper。
 *
 * 当元素 [StyleVisual] 含圆角 / 渐变 / mask（SV styled 效果）时走 `ROUNDED_RECT` / `GRADIENT` styled batch，
 * 边框由 shader 内描边绘制；否则退化为原有「1 次背景 fillRect + 4 条边框 fillRect」路径，
 * 保持现有像素与单测计数零回归。
 *
 * 原为包私有 helper，迁 Kotlin 后用 `internal object` 保留同等可见性（消费方均同模块 Kotlin 渲染器）。
 */
@Suppress("MagicNumber", "LongParameterList")
internal object ControlSurfaceRenderer {

    /**
     * 绘制控件背景 + 边框。
     *
     * @param themeBg          主题兜底背景色（DSL backgroundColor 不透明时优先）
     * @param borderColor      边框色（调用方已按 focus / 覆盖解析）
     * @param themeBorderWidth 退化路径的边框宽度（主题 borderWidth）；styled 路径优先用 SV.borderWidth
     */
    fun drawSurface(
        bridge: UiRenderBridge,
        element: UIElement<*>,
        box: Box,
        themeBg: Int,
        borderColor: Int,
        themeBorderWidth: Float,
    ) {
        val visual = element.style().visual()

        var bg = element.style().backgroundColor()
        if ((bg ushr 24) == 0) {
            bg = themeBg
        }

        if (hasStyled(visual)) {
            drawStyled(bridge, box, visual, bg, borderColor, themeBorderWidth)
            return
        }

        // 退化路径：背景 + 4 条边框（与历史实现逐像素一致）
        bridge.fillRect(box.x(), box.y(), box.width(), box.height(), bg)
        val bw = themeBorderWidth
        bridge.fillRect(box.x(), box.y(), box.width(), bw, borderColor)
        bridge.fillRect(box.x(), box.y() + box.height() - bw, box.width(), bw, borderColor)
        bridge.fillRect(box.x(), box.y(), bw, box.height(), borderColor)
        bridge.fillRect(box.x() + box.width() - bw, box.y(), bw, box.height(), borderColor)
    }

    /** 是否含圆角 / 渐变 / mask（边框单独宽度不触发 styled，沿用退化 4 边以保持像素一致）。 */
    private fun hasStyled(visual: StyleVisual): Boolean = !visual.cornerRadii().isZero() ||
        visual.gradientType() != GradientType.NONE ||
        visual.maskType() != MaskType.NONE

    /** styled batch：圆角 / 渐变 / mask 由 shader 处理；边框宽度优先取 SV，回退主题。 */
    private fun drawStyled(
        bridge: UiRenderBridge,
        box: Box,
        visual: StyleVisual,
        bg: Int,
        borderColor: Int,
        themeBorderWidth: Float,
    ) {
        val bw = if (visual.borderWidth() > 0f) visual.borderWidth() else themeBorderWidth
        val fill = StyledFill.builder()
            .cornerRadii(visual.cornerRadii().toArray())
            .borderWidth(bw)
            .borderColor(borderColor)
            .gradientType(visual.gradientType())
            .gradientAngleRadians(visual.gradientAngleRadians())
            .gradientStops(visual.gradientStops())
            .radialCenterX(visual.radialCenterX())
            .radialCenterY(visual.radialCenterY())
            .radialRadius(visual.radialRadius())
            .maskType(visual.maskType())
            .maskParam1(visual.maskParam1())
            .maskParam2(visual.maskParam2())
            .maskParam3(visual.maskParam3())
            .build()
        val cmdType = if (visual.gradientType() != GradientType.NONE) {
            RenderCommandType.GRADIENT
        } else {
            RenderCommandType.ROUNDED_RECT
        }
        val cmd = if (cmdType == RenderCommandType.GRADIENT) {
            RenderCommand.gradient(box.x(), box.y(), box.width(), box.height(), bg, RenderCommand.NO_CLIP, fill)
        } else {
            RenderCommand.roundedRect(box.x(), box.y(), box.width(), box.height(), bg, RenderCommand.NO_CLIP, fill)
        }
        val batch = RenderBatch(cmdType, null, RenderCommand.NO_CLIP, listOf(cmd))
        bridge.submitBatch(batch)
    }
}
